/**
 * ProgramWorkstationModalHost — opens the workstation window over any page (docs/PLAN-windows-workstation-20261009.md).
 *
 * Every surface that offers a desktop program's workstation (the Legal apps rows, the chat's card, onboarding) sends
 * PROGRAM_WORKSTATION_EVENT; this host, on the first download, keeps the request and draws the window once its
 * chunk has arrived (programWorkstationDoor.ts). A second request for the same workstation while its window is open
 * keeps that window (a second pass would only be told the screen is in use — by its own window).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useProgramWorkstationModule } from '../services/programWorkstation/programWorkstationDoor';
import {
  PROGRAM_WORKSTATION_EVENT,
  programWorkstationRequestOf,
  type ProgramWorkstationRequest,
} from '../services/programWorkstation/openProgramWorkstation';

export const ProgramWorkstationModalHost: React.FC = () => {
  const [request, setRequestState] = useState<ProgramWorkstationRequest | null>(null);
  const requestRef = useRef<ProgramWorkstationRequest | null>(null);
  const setRequest = (next: ProgramWorkstationRequest | null) => {
    requestRef.current = next;
    setRequestState(next);
  };
  const surface = useProgramWorkstationModule(request !== null);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const asked = programWorkstationRequestOf((e as CustomEvent).detail);
      if (!asked) return;
      const open = requestRef.current;
      if (open && open.accountId === asked.accountId) return;
      setRequest(asked);
    };
    document.addEventListener(PROGRAM_WORKSTATION_EVENT, onEvent as EventListener);
    return () => document.removeEventListener(PROGRAM_WORKSTATION_EVENT, onEvent as EventListener);
  }, []);

  if (!request || !surface) return null;
  const Modal = surface.default;
  return <Modal request={request} onClose={() => setRequest(null)} />;
};

export default ProgramWorkstationModalHost;
